import { defineStore, getActivePinia } from "pinia";
import global from "@/global";
import Swal from 'sweetalert2';
import { useOnboardingStore } from "@/stores/onboarding";

export const useAuthStore = defineStore("auth", {
  state: () => {
    const storedUser = localStorage.getItem("user");
    return {
      //v initialize state from local storage to enable user to stay logged in
      user: storedUser ? JSON.parse(storedUser) : null,
      // Added new state property
      hasSeenInstructions: JSON.parse(localStorage.getItem("hasSeenInstructions")) || false,
    };
  },
  actions: {
    // Refresh user data from API
    async refreshUserData() {
      if (!this.user || !this.user.accessToken) {
        return;
      }
      
      await this.fetchUserData();
    },

    //v Login
    async login(payload) {
      //v Get payload
      const { email, password } = payload;

      const formData = new FormData();
      formData.append("email", email.trim());
      formData.append("password", password);

      //v Send a request to get a user using email and password
      const res = await fetch(`${global.API_URL}/auth`, {
        method: "POST",
        body: formData,
      });

      //v If res comes with bad request i.e Invalid Credentials throw error with msg
      if (!res.ok) {
        const errorMessage = await res.json();
        throw new Error(errorMessage.error);
      }

      const user = await res.json();

      //v Send a request to get user info
      const userRes = await fetch(`${global.API_URL}/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      //v Throw Error on bad request
      if (!userRes.ok) {
        const errorMessage = await userRes.json();
        if (errorMessage.error) {
          throw new Error(errorMessage.error);
        } else {
          throw new Error("Something went wrong, please try again later!");
        }
      }

      const userResData = await userRes.json();
      
      // Extract the user data from the nested structure
      const userData = userResData.data || userResData;
      
      //v Create full user object
      let fullUser = {};
      fullUser.id = userData.id;
      fullUser.firstName = userData.first_name;
      fullUser.lastName = userData.last_name;
      fullUser.email = userData.email;
      fullUser.profilePicture = userData.avatar_url;
      fullUser.role = userData.role;
      fullUser.accessToken = user.token;

      // Fetch organization data to get the organization ID
      try {
        const orgRes = await fetch(`${global.API_URL}/organizations`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });

        if (orgRes.ok) {
          const orgData = await orgRes.json();
          if (orgData && orgData.data) {
            fullUser.organization_id = orgData.data.id;
          }
        }
      } catch (error) {
        console.error("Failed to fetch organization data during login:", error);
      }

      //v Save response to the local storage
      localStorage.setItem("user", JSON.stringify(fullUser));
    
      // Handle different user roles
      if (userData.role === 'owner') {
        // For owners, check subscription status from the me API response
        const subscriptionStatus = userData.subscription?.status || 'inactive';
        fullUser.subscriptionStatus = subscriptionStatus;
        
        // If subscription is not active, redirect to payment
        if (subscriptionStatus !== 'active') {
          this.user = fullUser;
          return {
            isAuthenticated: true,
            shouldRedirectToPayment: true
          };
        }
        
        // If subscription is active, proceed with normal flow
        this.user = fullUser;
        
        // Check onboarding completion status
        const onboardingStore = useOnboardingStore();
        await onboardingStore.fetchRouteStatuses();

        // Check if all onboarding steps are completed
        const onboardingRoutes = [
          '/onboarding/welcome',
          '/onboarding/organization',
          '/onboarding/about-you',
          // '/onboarding/self-assessment',
          '/onboarding/team'
        ];

        const allStepsCompleted = onboardingRoutes.every(route => 
          onboardingStore.getRouteStatus(route) === 1
        );

        // Return the completion status to handle redirection in the component
        return {
          isAuthenticated: true,
          shouldRedirectToOnboarding: !allStepsCompleted,
          firstIncompleteStep: !allStepsCompleted ? 
            onboardingRoutes.find(route => onboardingStore.getRouteStatus(route) !== 1) : null
        };
      } else if (userData.role === 'member') {
        // For members, check subscription status before allowing login
        try {
          const subscriptionRes = await fetch(
            `${global.API_URL}/check_subscription_status?id=${userData.id}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }
          );

          if (!subscriptionRes.ok) {
            const errorMessage = await subscriptionRes.json();
            throw new Error(errorMessage.message || "Failed to check subscription status");
          }

          const subscriptionData = await subscriptionRes.json();
          fullUser.subscriptionStatus = subscriptionData.login ? "active" : "inactive";

          // If the subscription is inactive, prevent login and show message
          if (fullUser.subscriptionStatus === "inactive") {
            Swal.fire({
              icon: 'warning',
              title: 'Subscription Expired',
              html: "Please contact your Organization Administrator to renew the subscription.<br><br>If you have problems renewing, reach out to our Help Desk at: help@askafundraiser.com",
              showConfirmButton: false,
              showCloseButton: true,
              customClass: {
                closeButton: 'swal2-close-custom'
              },
              didOpen: () => {
                const closeButton = document.querySelector('.swal2-close');
                if (closeButton) {
                  closeButton.innerHTML = '×';
                  closeButton.style.fontSize = '42px';
                  closeButton.style.fontWeight = 'normal';
                  closeButton.style.lineHeight = '1';
                }
                const popup = document.querySelector('.swal2-popup');
                if (popup) {
                  popup.style.paddingBottom = '2rem';

                }
              }
            });

            this.logout(); // to log out
            throw new Error("Please contact your Organization Administrator to renew the subscription.\n\nIf you have problems renewing, reach out to our Help Desk at: help@askafundraiser.com");
          }

          // If subscription is active, proceed with login
          this.user = fullUser;
          return {
            isAuthenticated: true,
            shouldRedirectToOnboarding: false,
            firstIncompleteStep: null
          };
        } catch (error) {
          console.error("Error checking subscription status:", error);
          // If error is already thrown above, rethrow it
          if (error.message && (error.message.includes("not active subscription") || error.message.includes("checking subscription") || error.message.includes("Please contact organization admin"))) {
            throw error;
          }
          // Otherwise, logout and throw generic error
          this.logout();
          throw new Error("Failed to verify subscription status. Please try again later.");
        }
      }

      // Initialize skipped modules store
      try {
        const { useSkippedModulesStore } = require('@/stores/skippedModules');
        const skippedModulesStore = useSkippedModulesStore();
        await skippedModulesStore.initializeOnLogin();
        
        // Verify initialization was successful
        if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
          // Try one more time with a delay to ensure token is ready
          await new Promise(resolve => setTimeout(resolve, 500));
          await skippedModulesStore.fetchSkippedModules(true);
        }
      } catch (error) {
        console.error("Failed to initialize skipped modules store:", error);
      }
      
      // Initialize projects store to fetch new projects
      try {
        const { useUserProjectsStore } = require('@/stores/projects');
        const projectsStore = useUserProjectsStore();
        await projectsStore.initializeWithAllData();
      } catch (error) {
        console.error("Failed to initialize projects store:", error);
      }
    },

    //v Sign Up
    async signUp(payload) {
      //v Get payload
      const { firstName, lastName, email, password, conformPassword } = payload;

      const formData = new FormData();
      formData.append("user[name]", `${firstName.trim()} ${lastName.trim()}`);
      formData.append("user[email]", email.trim());
      formData.append("user[password]", password);
      formData.append("user[password_confirmation]", conformPassword);
      formData.append("user[terms_of_service]", 1);

      //v Send a request to create a new user
      const res = await fetch(`${global.API_URL}/users`, {
        method: "POST",
        body: formData,
      });

      //V If res comes with bad request i.e Account with same email throw error with msg
      if (!res.ok) {
        const errorMessage = await res.json();
        // console.log(errorMessage.errors.email);
        throw new Error(`Email ${errorMessage.errors.email[0]}!`);
      }

      const user = await res.json();

      //v Send a request to get user info
      const userRes = await fetch(`${global.API_URL}/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.user.api_tokens[0].token}`,
        },
      });

      //v Throw Error on bad request
      if (!userRes.ok) {
        const errorMessage = await userRes.json();
        // console.log(errorMessage);
        if (errorMessage.error) {
          throw new Error(errorMessage.error);
        } else {
          throw new Error("Something went wrong, please try again later!");
        }
      }

      const userResData = await userRes.json();

      // Extract the user data from the nested structure
      const userData = userResData.data || userResData;

      //v Create full user object
      let fullUser = {};

      fullUser.firstName = userData.first_name;
      fullUser.lastName = userData.last_name;
      fullUser.email = userData.email;
      fullUser.profilePicture = userData.avatar_url;
      fullUser.role = userData.role;
      fullUser.accessToken = user.user.api_tokens[0].token;

      //v Save response to the state
      this.user = fullUser;

      //v Save response to the local storage
      localStorage.setItem("user", JSON.stringify(fullUser));

      // Initialize skipped modules store
      try {
        const { useSkippedModulesStore } = require('@/stores/skippedModules');
        const skippedModulesStore = useSkippedModulesStore();
        await skippedModulesStore.initializeOnLogin();
      } catch (error) {
        console.error("Failed to initialize skipped modules store:", error);
      }
    },
    //v Logout
    logout() {      
      // Import skipped modules store
      const { useSkippedModulesStore } = require('@/stores/skippedModules');
      const skippedModulesStore = useSkippedModulesStore();
      
      // Reset the skipped modules store explicitly
      if (skippedModulesStore) {
        skippedModulesStore.reset();
      }
      
      this.user = null;
      //v Clear local storage
      localStorage.clear();
      //v Clear session storage
      sessionStorage.clear();
      //v Clear Pinia stores
      getActivePinia()._s.forEach((store) => store.$reset());
    },

    async fetchUserData() {
      if (!this.user || !this.user.accessToken) return;

      try {
        const res = await fetch(`${global.API_URL}/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.user.accessToken}`,
          },
        });

        if (res.ok) {
          const responseData = await res.json();          
          // Extract the user data from the nested structure
          const userData = responseData.data || responseData;
          
          // Fetch organization data to get the organization ID
          const orgRes = await fetch(`${global.API_URL}/organizations`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.user.accessToken}`,
            },
          });

          let organizationId = null;
          if (orgRes.ok) {
            const orgData = await orgRes.json();
            if (orgData && orgData.data) {
              organizationId = orgData.data.id;
            }
          }
          
          // Update the user object with the latest data
          this.user = {
            ...this.user,
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            email: userData.email,
            profilePicture: userData.avatar_url,
            role: userData.role,
            subscriptionStatus: userData.subscription_status,
            organization_id: organizationId
          };
          
          // Update localStorage with the latest user data
          localStorage.setItem("user", JSON.stringify(this.user));
        }
      } catch (error) {
        console.error("Failed to fetch user data", error);
      }
    },
  
    // Added new action to mark instructions as seen
    markInstructionsAsSeen() {
      this.hasSeenInstructions = true;
      localStorage.setItem("hasSeenInstructions", JSON.stringify(true));
    },
  },
});
